import React from 'react';
import autosize from 'autosize';

import { createClassName } from 'utils/createClassName';
import { setCustomTimeout } from 'utils/setCustomTimeout';

import './textInputControlStyles.scss';

type Props = {
  value: string;
  name?: string;
  type?: 'email' | 'password' | 'text' | 'date';
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
  placeholder?: string;
  invalid?: boolean;
  testId?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  maxLength?: number;
  readOnly?: boolean;
  multiLine?: boolean;
  hidePlaceholderOnFocus?: boolean;
  onValueChange(nextValue: string): void;
  onBlur?(): void;
};

export const TextInputControlComponent = (props: Props) => {
  const {
    value,
    type = 'text',
    inputMode,
    placeholder,
    onValueChange,
    onBlur,
    testId,
    autoFocus,
    invalid,
    disabled,
    maxLength,
    readOnly,
    multiLine,
    hidePlaceholderOnFocus,
  } = props;

  const inputNodeRef = React.useRef<HTMLInputElement>(null);

  const textAreaNodeRef = React.useRef<HTMLTextAreaElement>(null);

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onValueChange(event.currentTarget.value);
    },
    [onValueChange]
  );

  const className = createClassName('textInputControl')({
    invalid,
    readOnly,
    multiLine,
    hidePlaceholderOnFocus,
  });

  React.useLayoutEffect(() => {
    const { current: textAreaNode } = textAreaNodeRef;

    if (textAreaNode) {
      autosize(textAreaNode);
    }
  }, []);

  React.useEffect(() => {
    const { current: inputNode } = inputNodeRef;

    const { current: textAreaNode } = textAreaNodeRef;

    if (autoFocus) {
      return setCustomTimeout(() => {
        if (inputNode) {
          inputNode.focus();
        }

        if (textAreaNode) {
          textAreaNode.focus();
        }
      }, 100);
    }
  }, [autoFocus]);

  if (multiLine) {
    return (
      <textarea
        ref={textAreaNodeRef}
        disabled={disabled}
        className={className}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        readOnly={readOnly}
        onChange={onChange}
        onBlur={onBlur}
        data-testid={testId}
        data-invalid={invalid}
      />
    );
  }

  return (
    <input
      ref={inputNodeRef}
      type={type}
      disabled={disabled}
      className={className}
      value={value}
      placeholder={placeholder}
      inputMode={inputMode}
      maxLength={maxLength}
      readOnly={readOnly}
      onChange={onChange}
      onBlur={onBlur}
      data-testid={testId}
      data-invalid={invalid}
    />
  );
};
